<template>
	<div
		class="offer-verified__body"
		:class="{ 'offer-verified__body--hotel-only': !isPackage }"
	>
		<div
			:class="[
				'offer-verified__travel-details-container',
				{
					'offer-verified__travel-details-container-hotel-only': !isPackage,
				},
			]"
		>
			<OfferVerifiedTravelDetails
				:offer-data="offerData"
				:outbound-flight-segments="outboundFlightSegments"
				:inbound-flight-segments="inboundFlightSegments"
			/>
			<div :class="{ 'offer-verified__flight-details-container': isPackage && isVerified }">
				<OfferFlightDetailsModal
					v-if="isPackage && isVerified && !isNotAvailable"
					class="offer-verified__flight-details-modal"
					:inbound-flight-segments="inboundFlightSegments"
					:outbound-flight-segments="outboundFlightSegments"
					@flight-details-modal-open="flightDetailsAnalytics"
				/>
				<LinkButton
					v-if="isPackage && isVerified"
					v-show="alternativeFlights && isPackage"
					:label="'Alternative Flüge'"
					:with-icon="true"
					bold
					class="offer-verified__alternative-flight-button"
					@click="emit('show-alternative-flights')"
				/>
			</div>
		</div>
		<OfferVerifiedDivider />
		<div
			:class="[
				'offer-verified__room-container',
				{
					'offer-verified__room-container-hotel-only': !isPackage,
				},
			]"
		>
			<OfferVerifiedRoomDetails
				:offer-data="offerData"
				:operator="operator"
			/>
			<OfferDetailsCatalogueModal
				v-if="isVerified && !isNotAvailable"
				:catalogue-info-data="catalogueInfoData"
				:is-loading="isCatalogueInfoLoading"
				:client="client"
				class="offer-verified__catalogue-modal"
				@operator-change="getCatalogueInfo"
				@catalogue-modal-open="handleShowHotelInfosClick"
			/>
		</div>
		<OfferVerifiedDivider />
		<div class="offer-verified__price-details-container">
			<OfferVerifiedPriceDetails
				:offer-data="offerData"
				:client="client"
				:ibe-checkout-url="isVerified ? ibeCheckoutUrl : ''"
				@verify-offer="emit('verify-offer')"
			/>
		</div>
	</div>
</template>

<script lang="ts" setup>
import { ref } from 'vue';
import type { ClientType, TourOperator } from '@/interfaces/common';
import type { MergedOfferItemData, VerifiedFlightSegment, AlternativeFlightsDataArray } from '@interfaces/offer';
import type { HotelCatalogueType } from '@interfaces/hotel-catalogue-types';
import LinkButton from '@lmt-rpb/Atoms/LinkButton/LinkButton.vue';
import OfferVerifiedTravelDetails from './OfferVerifiedTravelDetails.vue';
import OfferVerifiedRoomDetails from './OfferVerifiedRoomDetails.vue';
import OfferVerifiedPriceDetails from './OfferVerifiedPriceDetails.vue';
import { getCatalogInfo } from '@/components/common/services/catalogInfoService';
import OfferVerifiedDivider from './OfferVerifiedDivider.vue';
import { useOfferVerifiedInjection } from '../injectionProps';
import SelectContentAnalytics from '@/js/modules/analytics/SelectContentAnalytics';
import OfferDetailsCatalogueModal from 'src/molecules/OfferDetailsCatalogueModal/OfferDetailsCatalogueModal.vue';
import OfferFlightDetailsModal from 'src/molecules/OfferFlightDetailsModal/OfferFlightDetailsModal.vue';

interface Props {
	offerData: MergedOfferItemData;
	client?: ClientType;
	operator: TourOperator;
	outboundFlightSegments: VerifiedFlightSegment[];
	inboundFlightSegments: VerifiedFlightSegment[];
	ibeCheckoutUrl: string;
	hotelData: {
		Hotel: {
			IffCode: string;
		};
	};
	alternativeFlightsLoading: boolean;
	alternativeFlights: AlternativeFlightsDataArray | boolean;
}

const props = withDefaults(defineProps<Props>(), {
	client: 'at',
	alternativeFlightsLoading: false,
});

const emit = defineEmits(['show-alternative-flights', 'verify-offer']);
const isCatalogueInfoLoading = ref(true);
const catalogueInfoData = ref<HotelCatalogueType | null>(null);
const operatorCodeMatching = ref(true);
const { isNotAvailable, isVerified, isPackage } = useOfferVerifiedInjection();

const getCatalogueInfo = async (code?: string) => {
	isCatalogueInfoLoading.value = true;

	try {
		const result = await getCatalogInfo(props.offerData, props.hotelData, props.operator, operatorCodeMatching.value, code);
		catalogueInfoData.value = result;
		if (result && !result.OfferCodeMatches) {
			operatorCodeMatching.value = false;
		}
	} catch (error) {
		console.error('Error fetching catalog info:', error);
		catalogueInfoData.value = null;
	} finally {
		isCatalogueInfoLoading.value = false;
	}
};

const flightDetailsAnalytics = () => {
	SelectContentAnalytics.selectContent({
		cId: 'offerlist_verifiedoffer_travel_details',
		cType: 'ux_hotelpage_show_details',
	});
};

const offerDetailsAnalytics = () => {
	SelectContentAnalytics.selectContent({
		cId: 'offerlist_verifiedoffer_offer_details',
		cType: 'ux_hotelpage_show_details',
	});
};

const handleShowHotelInfosClick = () => {
	offerDetailsAnalytics();
	if (!catalogueInfoData.value) {
		getCatalogueInfo();
	}
};
</script>

<style lang="scss" scoped>
.offer-verified {
	&__body {
		padding: 1.6rem;
		display: flex;
		gap: 1.6rem;
		flex-direction: column;
	}

	&__travel-details-container {
		width: 100%;
	}

	&__room-container {
		display: flex;
		justify-content: flex-start;
		gap: 1.6rem;
		flex-direction: column;
	}

	&__catalogue-modal {
		margin-top: auto;
		width: 100%;
	}

	&__hotel-infos-button {
		max-width: 42rem;
	}

	&__flight-details-container {
		display: flex;
		justify-content: space-between;
		gap: 0.8rem;
		margin-top: 1.6rem;

		& > * {
			flex: 1;
		}

		@include media-query-up($breakpoint-small) {
			gap: 1.6rem;
		}
	}

	@media (min-width: $breakpoint-small) {
		&__body {
			flex-direction: row;
			gap: 1.2rem;
		}

		&__travel-details-container {
			display: flex;
			flex-direction: column;
			min-width: 18.2rem;
			max-width: 21rem;

			&-hotel-only {
				min-width: 23rem;
			}
		}

		&__alternative-flight-button {
			border-top: 1px solid $color-primary-l4;
			padding-top: 1.6rem;
		}

		&__flight-details-container {
			flex-direction: column;
		}

		&__alternative-flights-loading {
			margin-top: 3.4rem;
			margin-right: 2.1rem;
			padding-top: 1.6rem;
		}

		&__price-details-container {
			display: flex;
			flex-direction: column;
			place-content: flex-end space-between;
			min-width: 20rem;
		}

		&__room-container {
			min-width: 25.5rem;
			width: 100%;

			&-hotel-only {
				min-width: initial;
			}
		}
	}

	@media (min-width: $breakpoint-medium) {
		&__body {
			padding: 2.4rem;
			gap: 1.6rem;
		}

		&__travel-details-container {
			justify-content: space-between;
			min-width: 33rem;
			width: 100%;
		}

		&__flight-details-container {
			flex-direction: row;
		}

		&__alternative-flight-button {
			border-top: 0;
			padding-top: 0;
		}

		&__alternative-flights-loading {
			margin: 0;
			padding: 0;
			margin-right: 2rem;
		}

		&__price-container {
			width: 33.33%;
		}

		&__price-details-container {
			width: 100%;
		}
	}
}
</style>
